import { useLoaderData, useRevalidator, useRouteError } from "react-router-dom";
import GenericErrorDisplay, { useSetError } from "../generic-error";
import TransmedCoordinatorService from "../../service/transmed/process/coordinator";
import OutputTransmedCoordinator from "../../dtos/transmed/process/coordinator";
import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAdd,
  faChevronDown,
  faChevronUp,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import SearchBox from "../../components/inputs/search-box";
import SwitchInput from "../../components/inputs/checkbox/switch";
import { CircularProgress } from "@mui/material";
import InputDeleteDialog from "../../components/dialog/delete";
import TransmedValidatorService from "../../service/transmed/process/validator";
import OutputTransmedValidator from "../../dtos/transmed/process/validator";
import TransmedProcessService from "../../service/transmed/process";
import TransmedEdition from "../../dtos/transmed/process/edition";

export const path = "transmed/staff";

export async function loader() {
  const edition = new TransmedProcessService().read.edition();
  const coordinators = new TransmedCoordinatorService().read.current();
  const validators = new TransmedValidatorService().read.current();
  return [await edition, await coordinators, await validators];
}

export function Component() {
  const coordinatorService = new TransmedCoordinatorService();
  const validatorService = new TransmedValidatorService();
  const revalidator = useRevalidator();
  const setErrorOnly = useSetError();
  const setError = (error: unknown) => {
    setErrorOnly(error);
    revalidator.revalidate();
  };

  const [edition, coordinators, validators] = useLoaderData() as [
    TransmedEdition,
    OutputTransmedCoordinator[],
    OutputTransmedValidator[],
    boolean
  ];

  const [loading, setLoading] = useState<boolean>(false);
  const [dialogCallback, setDialogCallback] = useState<
    (() => void | Promise<void>) | null
  >(null);

  const [selected, setSelected] = useState<
    "coordinators" | "validators" | null
  >(null);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        justifyContent: "flex-start",
        gap: "var(--gap-medium)",
      }}
    >
      <h1>Staff transmed</h1>
      {edition.participation_proof_deadline < new Date() && (
        <div style={{ color: "red" }}>
          <strong>Atenție!</strong> În acest moment editezi staff-ul ediției
          care s-a terminat.
        </div>
      )}
      <div
        className="surface"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          justifyContent: "flex-start",
          gap: "var(--gap-medium)",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h3>Coordonatori</h3>
          <button
            className="primary text button"
            onClick={() =>
              setSelected((value) =>
                value === "coordinators" ? null : "coordinators"
              )
            }
          >
            <FontAwesomeIcon
              icon={selected === "coordinators" ? faChevronUp : faChevronDown}
            />
          </button>
        </div>
        {selected === "coordinators" && (
          <>
            <SearchBox
              placeholder="Coordonator nou"
              icon={
                loading || revalidator.state === "loading" ? (
                  <CircularProgress />
                ) : (
                  <FontAwesomeIcon icon={faAdd} />
                )
              }
              onSearch={async (value) => {
                value = value.trim();
                if (value.length === 0) return;

                setError("");
                setLoading(true);

                try {
                  await coordinatorService.create.coordinator(value);
                  revalidator.revalidate();
                } catch (error) {
                  setError(error);
                } finally {
                  setLoading(false);
                }
              }}
            />
            {coordinators.length === 0 ? (
              <div className="label">
                Adaugă coordonatori introducând id-ul public în caseta de mai
                sus și apăsând pe butonul <b>+</b> din partea dreaptă
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  justifyContent: "flex-start",
                  gap: "var(--gap-small)",
                }}
              >
                {coordinators
                  .sort((a, b) =>
                    a.user.public_id.localeCompare(b.user.public_id)
                  )
                  .map((coordinator) => (
                    <div
                      key={coordinator.id}
                      className="surface"
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        flexWrap: "wrap",
                        gap: "var(--gap-xs)",
                      }}
                    >
                      <div className="label" style={{ flex: 1 }}>
                        {coordinator.lc.name_short}
                      </div>
                      <span style={{ flex: 5 }}>
                        <b>
                          {coordinator.user.first_name +
                            " " +
                            coordinator.user.last_name}
                        </b>
                        <i> ({coordinator.user.public_id})</i>
                      </span>
                      <SwitchInput
                        checked={coordinator.is_assistant}
                        label="Asistent"
                        disabled={loading || revalidator.state === "loading"}
                        onChange={async (event) => {
                          setError("");
                          setLoading(true);
                          try {
                            await coordinatorService.update.isAssistant(
                              coordinator.id,
                              event.target.checked
                            );
                            revalidator.revalidate();
                          } catch (error) {
                            setError(error);
                          } finally {
                            setLoading(false);
                          }
                        }}
                      />
                      <button
                        className="primary button"
                        style={{ flex: 0, ...{ "--color": "red" } }}
                        onClick={() =>
                          setDialogCallback(() => async () => {
                            setError("");
                            setLoading(true);
                            try {
                              await coordinatorService.delete.coordinator(
                                coordinator.id
                              );
                              revalidator.revalidate();
                            } catch (error) {
                              setError(error);
                            } finally {
                              setLoading(false);
                            }
                          })
                        }
                      >
                        {loading || revalidator.state === "loading" ? (
                          <CircularProgress sx={{ color: "inherit" }} />
                        ) : (
                          <FontAwesomeIcon icon={faTrash} />
                        )}
                      </button>
                    </div>
                  ))}
              </div>
            )}
          </>
        )}
      </div>
      <div
        className="surface"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          justifyContent: "flex-start",
          gap: "var(--gap-medium)",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h3>Validatori și verificatori</h3>
          <button
            className="primary text button"
            onClick={() =>
              setSelected((value) =>
                value === "validators" ? null : "validators"
              )
            }
          >
            <FontAwesomeIcon
              icon={selected === "validators" ? faChevronUp : faChevronDown}
            />
          </button>
        </div>
        {selected === "validators" && (
          <>
            <SearchBox
              placeholder="Validator nou"
              icon={
                loading || revalidator.state === "loading" ? (
                  <CircularProgress />
                ) : (
                  <FontAwesomeIcon icon={faAdd} />
                )
              }
              onSearch={async (value) => {
                value = value.trim();
                if (value.length === 0) return;

                setError("");
                setLoading(true);

                try {
                  await validatorService.create.validator(value);
                  revalidator.revalidate();
                } catch (error) {
                  setError(error);
                } finally {
                  setLoading(false);
                }
              }}
            />
            {validators.length === 0 ? (
              <div className="label">
                Adaugă validatori/verificatori introducând id-ul public în
                caseta de mai sus și apăsând pe butonul <b>+</b> din partea
                dreaptă
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  justifyContent: "flex-start",
                  gap: "var(--gap-small)",
                }}
              >
                {validators
                  .sort((a, b) =>
                    a.user.public_id.localeCompare(b.user.public_id)
                  )
                  .map((validator) => (
                    <div
                      key={validator.id}
                      className="surface"
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        flexWrap: "wrap",
                        gap: "var(--gap-xs)",
                      }}
                    >
                      <div className="label" style={{ flex: 1 }}>
                        {validator.lc.name_short}
                      </div>
                      <span style={{ flex: 5 }}>
                        <b>
                          {validator.user.first_name +
                            " " +
                            validator.user.last_name}
                        </b>
                        <i> ({validator.user.public_id})</i>
                      </span>
                      <SwitchInput
                        checked={validator.allow_edit}
                        label="Permite validarea"
                        disabled={loading || revalidator.state === "loading"}
                        onChange={async (event) => {
                          setError("");
                          setLoading(true);
                          try {
                            await validatorService.update.allowEdit(
                              validator.id,
                              event.target.checked
                            );
                            revalidator.revalidate();
                          } catch (error) {
                            setError(error);
                          } finally {
                            setLoading(false);
                          }
                        }}
                      />
                      <button
                        className="primary button"
                        style={{ flex: 0, ...{ "--color": "red" } }}
                        onClick={() =>
                          setDialogCallback(() => async () => {
                            setError("");
                            setLoading(true);
                            try {
                              await validatorService.delete.validator(
                                validator.id
                              );
                              revalidator.revalidate();
                            } catch (error) {
                              setError(error);
                            } finally {
                              setLoading(false);
                            }
                          })
                        }
                      >
                        {loading || revalidator.state === "loading" ? (
                          <CircularProgress sx={{ color: "inherit" }} />
                        ) : (
                          <FontAwesomeIcon icon={faTrash} />
                        )}
                      </button>
                    </div>
                  ))}
              </div>
            )}
          </>
        )}
      </div>
      <InputDeleteDialog
        dialogCallback={dialogCallback}
        onClose={() => setDialogCallback(null)}
      />
    </div>
  );
}
Component.displayName = "page-account-transmed-staff";

export function ErrorBoundary() {
  const error = useRouteError();
  return <GenericErrorDisplay error={error} />;
}
ErrorBoundary.displayName = "page-account-transmed-staff-error";
